import type { ShippingCost } from "types";
import type {
  SalesResolvePayload,
  SalesResolveShippingOption,
  SalesResolveSpecialFee,
} from "../types/sales";
import {
  buildSalesResolveOrderItems,
  type BuildSalesResolveCatalogRow,
  type BuildSalesResolveCustomRow,
} from "./buildSalesResolveOrderItems";
import { readResolveTaxable, resolveTaxableFromSources, findCatalogRowById, resolveSpecialFeeRequiredForPayload } from "../utils/readResolveTaxable";
import type { SalesResolveBuyerContext } from "./pickSalesResolveBuyerContext";
import { buildCheckoutOrderPartyFields } from "../checkout/checkoutSalesResolveHelpers";

type ShipPayload = {
  shipping_option?: unknown[] | null;
  isUps?: boolean;
  isManual?: boolean;
};

type FeePayload = {
  specialfee?: unknown[] | null;
};

export type BuildSalesResolveInput = {
  sellerOrgId: string;
  sellerMembershipId?: string;
  sellerGlobalUserId?: string;
  cartBuyerOrgId?: string | null;
  cartBuyerMembershipId?: string | null;
  cartBuyerGlobalUserId?: string | null;
  buyerContext: SalesResolveBuyerContext;
  signageId?: string;
  signageCompanyName?: string;
  billingAddress?: unknown;
  shippingAddress?: unknown;
  bothAddressSame?: boolean;
  inventorySource?: string;
  discountCode?: string;
  cartId?: string | null;
  orderId?: string | null;
  orderNumber?: string | null;
  orderTaxStatus?: string;
  selectedProducts?: BuildSalesResolveCatalogRow[] | null;
  customproducts?: BuildSalesResolveCustomRow[] | null;
  productObjs?: Parameters<typeof buildSalesResolveOrderItems>[0]["productObjs"];
  shipPayload?: ShipPayload | null;
  feePayload?: FeePayload | null;
  shippingCosts?: ShippingCost[];
  /** Fallback shipping rows when UI payload is empty (edit load). */
  fallbackShippingRows?: unknown[] | null;
  /** Fallback fee rows when UI payload is empty (edit load). */
  fallbackFeeRows?: unknown[] | null;
  /** Vendor special-fee catalog from GET — source of truth for taxable flags. */
  specialFeeCatalog?: unknown[] | null;
  manualUnitPriceMap: Record<string, number>;
  getUnitRetailPriceForPayload: (
    productId: string,
    variantId: string,
    originalUnitPrice: number,
    lineId?: string
  ) => number;
  getChangedUnitPriceForPayload: (
    productId: string,
    variantId: string,
    originalUnitPrice: number,
    lineId?: string
  ) => number;
};

const asNum = (v: unknown) => Number(v || 0);

function mapUiShipToResolve(
  r: Record<string, unknown>,
  shippingCosts: ShippingCost[] = []
): SalesResolveShippingOption {
  const id = String(r.shipping__id ?? r.shipping_id ?? "");
  const cost = shippingCosts.find((c) => String(c._id) === id) as
    | Record<string, unknown>
    | undefined;
  const fromRowPickup = r?.is_customer_pickup === true;
  const fromCostPickup = cost?.is_customer_pickup === true;
  const rowType = String(r.shipping_type ?? "");
  const costType = String(cost?.shipping_type ?? "");
  const shipping_taxable = resolveTaxableFromSources(
    [r, cost],
    ["shipping_taxable", "taxable", "is_taxable"],
    true
  );
  return {
    shipping_id: id,
    shipping_name: String(r.shipping_name ?? ""),
    shipping_type: rowType || costType || (id === "custom-manual" ? "MANUAL" : ""),
    shipping_amount: asNum(r.shipping_amount),
    shipping_taxable,
    shipping_delivery_date: String(
      r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""
    ),
    shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
    is_customer_pickup: fromRowPickup || fromCostPickup,
  };
}

function mapUiFeeToResolve(
  f: Record<string, unknown>,
  catalog?: unknown[] | null
): SalesResolveSpecialFee {
  const id = String(f.specialfee_id ?? f.id ?? f._id ?? "");
  const catalogRow = findCatalogRowById(catalog, id, ["id", "_id", "specialfee_id"]);
  const specialfee_taxable = resolveTaxableFromSources(
    [catalogRow, f],
    ["taxable", "is_taxable", "specialfee_taxable"],
    true
  );
  return {
    specialfee_id: id,
    specialfee_name: String(f.specialfee_name ?? ""),
    specialfee_amount: asNum(f.specialfee_amount),
    specialfee_taxable,
    required: resolveSpecialFeeRequiredForPayload(f, catalog),
  };
}

/**
 * Build Order-shaped payload for POST /orders/resolve (`order_type: "order"`).
 */
export function buildSalesResolvePayload(input: BuildSalesResolveInput): SalesResolvePayload {
  const shippingRowsFromPayload = Array.isArray(input.shipPayload?.shipping_option)
    ? input.shipPayload!.shipping_option!
    : [];
  const shippingRows =
    shippingRowsFromPayload.length > 0
      ? shippingRowsFromPayload
      : Array.isArray(input.fallbackShippingRows)
        ? input.fallbackShippingRows
        : [];

  const feeRowsFromPayload = Array.isArray(input.feePayload?.specialfee)
    ? input.feePayload!.specialfee!
    : [];
  const feeRows =
    feeRowsFromPayload.length > 0
      ? feeRowsFromPayload
      : Array.isArray(input.fallbackFeeRows)
        ? input.fallbackFeeRows
        : [];

  const buyer = input.buyerContext;

  const items = buildSalesResolveOrderItems({
    selectedProducts: input.selectedProducts,
    customproducts: input.customproducts,
    productObjs: input.productObjs,
    manualUnitPriceMap: input.manualUnitPriceMap,
    getUnitRetailPriceForPayload: input.getUnitRetailPriceForPayload,
    getChangedUnitPriceForPayload: input.getChangedUnitPriceForPayload,
    inventorySource: buyer.inventorySource || input.inventorySource || "signage",
  });

  const shippingOptions = (shippingRows as Record<string, unknown>[]).map((r) =>
    mapUiShipToResolve(r, input.shippingCosts || [])
  );
  const specialFees = (feeRows as Record<string, unknown>[]).map((f) =>
    mapUiFeeToResolve(f, input.specialFeeCatalog)
  );
  const shippingAmount = shippingOptions.reduce((s, o) => s + asNum(o.shipping_amount), 0);
  const sellerOrgId = String(input.sellerOrgId ?? "").trim();
  const partyFields = buildCheckoutOrderPartyFields({
    buyerContext: buyer,
    sellerOrgId,
    sellerMembershipId: input.sellerMembershipId,
    sellerGlobalUserId: input.sellerGlobalUserId,
    cartBuyerOrgId: input.cartBuyerOrgId,
    cartBuyerMembershipId: input.cartBuyerMembershipId,
    cartBuyerGlobalUserId: input.cartBuyerGlobalUserId,
  });

  return {
    ...(input.orderId ? { id: input.orderId } : {}),
    ...(input.orderNumber ? { order_number: input.orderNumber } : {}),
    ...partyFields,
    signage_id: String(input.signageId ?? sellerOrgId ?? partyFields.seller_org_id ?? "").trim(),
    signage_company_name: input.signageCompanyName ?? "",
    buyer_type: buyer.buyerType ?? "",
    brokerage_firm_id:
      partyFields.brokerage_firm_org_id ?? buyer.brokerageFirmId ?? null,
    brokerage_firm_name: buyer.brokerageFirmName ?? null,
    brokerage_firm_admin: "",
    team_member_id:
      partyFields.team_member_membership_id ??
      partyFields.team_member_id ??
      buyer.teamMemberId ??
      null,
    team_member_name: buyer.teamMemberName ?? "",
    office: buyer.office ?? undefined,
    customer_org_id:
      partyFields.customer_org_id ||
      partyFields.buyer_org_id ||
      buyer.buyer?.organizationId ||
      null,
    agent_id: partyFields.agent_membership_id ?? partyFields.agent_id ?? buyer.agentId ?? null,
    agent_name: buyer.agentName ?? null,
    customer_first_name: buyer.customerFirstName ?? "",
    customer_last_name: buyer.customerLastName ?? "",
    customer_email: buyer.customerEmail ?? "",
    customer_phone: buyer.customerPhone ?? "",
    cart_id: input.cartId ?? null,
    inventory_source: buyer.inventorySource || "signage",
    discount_code: String(input.discountCode ?? "").trim(),
    shipping_address: buyer.shippingAddress as SalesResolvePayload["shipping_address"],
    billing_address: buyer.billingAddress as SalesResolvePayload["billing_address"],
    both_address_same: !!input.bothAddressSame,
    items,
    order_type: "sales",
    shipping_amount: shippingAmount,
    shipping_method: {
      carrier_name: "",
      service_name: "",
      tracking_number: "",
      cost: shippingAmount,
    },
    order_tax_summary: {
      tax_status: input.orderTaxStatus ?? "",
      tax_inclusive: false,
    },
    order_charges: [
      {
        shipping_option: shippingOptions,
        specialfee: specialFees,
      },
    ],
  };
}
